import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  AccessLevel,
  DigitalSpecialist,
  DigitalSpecialistSession,
  DsUser,
  LLMTool,
  PaginatedResponse,
  SharedResourceType,
  Skill,
  SkillRelationships,
  ToolRelationships,
  UserInstance,
} from '@cwan-gpt-ui/models'
import { environment } from 'environments/environment'
import { catchError, take } from 'rxjs/operators'

// let DS_BASE_URL = `http://127.0.0.1:8080/digital-specialist-ws`
let DS_BASE_URL = `${environment.cwanGptWsBaseUrl}/digital-specialist-ws`

/**
 * This is the implementation of the digital specialist API.
 * IMPORTANT!  this should be only a _very_ thin layer over the raw API.
 *   - NO BUSINESS LOGIC HERE
 *   - NO DEPENDENCIES (other than models used in the API)
 */
@Injectable({
  providedIn: 'root',
})
export class DigitalSpecialistApi {
  constructor(private readonly http: HttpClient) { }

  public usePublicApi() {
    DS_BASE_URL = `${environment.publicApiBaseUrl}/digital-specialist-ws`
  }

  /**
   * WARNING - this does not return any details on skills or tools
   * @returns list of all specialists
   */
  getAllSpecialists(accessLevel: AccessLevel): Promise<DigitalSpecialist[]> {
    const url = `${DS_BASE_URL}/specialists?accessLevel=` + accessLevel
    return this.http
      .get<DigitalSpecialist[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getAll API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getSpecialistsWithPagination(
    accessLevel: AccessLevel,
    limit: number = 50,
    nextToken?: string
  ): Promise<PaginatedResponse<DigitalSpecialist>> {
    return this.getPaginatedResults<DigitalSpecialist>(
      'v2/specialists',
      limit,
      nextToken,
      { accessLevel: accessLevel.toString() }
    )
  }
  /**
   * @returns DS with all the details of the skills filled in
   */
  getSpecialist(username: string): Promise<DigitalSpecialist> {
    const url = `${DS_BASE_URL}/specialists/${username}`
    return this.http
      .get<DigitalSpecialist>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getByUsername API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  createSpecialist(specialist: DigitalSpecialist): Promise<DigitalSpecialist> {
    const url = `${DS_BASE_URL}/specialists`
    return this.http
      .post<DigitalSpecialist>(url, specialist)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/create API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  cloneSpecialist(
    username: string,
    newUsername: string
  ): Promise<DigitalSpecialist> {
    const url = `${DS_BASE_URL}/specialists/${username}/clone`
    return this.http
      .post<DigitalSpecialist>(
        url,
        {},
        {
          params: { newUsername: newUsername },
        }
      )
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/clone API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  updateSpecialist(specialist: DigitalSpecialist): Promise<DigitalSpecialist> {
    const url = `${DS_BASE_URL}/specialists/${specialist.username}`
    return this.http
      .put<DigitalSpecialist>(url, specialist)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/edit API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  deleteSpecialist(username: string): Promise<void> {
    const url = `${DS_BASE_URL}/specialists/${username}`
    return this.http
      .delete<void>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/delete API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getAvailableTools(): Promise<LLMTool[]> {
    const url = `${DS_BASE_URL}/tools`
    return this.http
      .get<LLMTool[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getAvailableTools API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getTool(id: string): Promise<LLMTool> {
    const url = `${DS_BASE_URL}/tools/${id}`
    return this.http
      .get<LLMTool>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getTool API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getToolRelationships(id: string): Promise<ToolRelationships> {
    const url = `${DS_BASE_URL}/tools/${id}/relationships`
    return this.http
      .get<ToolRelationships>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getToolRelationships API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  updateTool(tool: LLMTool): Promise<LLMTool> {
    const url = `${DS_BASE_URL}/tools/${tool.toolId}`
    return this.http
      .put<LLMTool>(url, tool)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/updateTool API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  createTool(tool: LLMTool): Promise<LLMTool> {
    const url = `${DS_BASE_URL}/tools`
    return this.http
      .post<LLMTool>(url, tool)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/createTool API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  deleteTool(id: string): Promise<void> {
    const url = `${DS_BASE_URL}/tools/${id}`
    return this.http
      .delete<void>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/deleteTool API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  createSkill(skill: Skill): Promise<Skill> {
    const url = `${DS_BASE_URL}/skills`
    return this.http
      .post<Skill>(url, skill)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/createSkill API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getSkills(): Promise<Skill[]> {
    const url = `${DS_BASE_URL}/skills`
    return this.http
      .get<Skill[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getSkills API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getSkillsWithPagination(
    limit: number = 50,
    nextToken?: string
  ): Promise<PaginatedResponse<Skill>> {
    return this.getPaginatedResults<Skill>('v2/skills', limit, nextToken)
  }

  updateSkill(skill: Skill): Promise<Skill> {
    const url = `${DS_BASE_URL}/skills/${skill.skillId}`
    return this.http
      .put<Skill>(url, skill)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/updateSkill API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getSkill(id: string): Promise<Skill> {
    const url = `${DS_BASE_URL}/skills/${id}`
    return this.http
      .get<Skill>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getSkill API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getSkillRelationships(id: string): Promise<SkillRelationships> {
    const url = `${DS_BASE_URL}/skills/${id}/relationships`
    return this.http
      .get<SkillRelationships>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getSkillRelationships API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  deleteSkill(id: string): Promise<void> {
    const url = `${DS_BASE_URL}/skills/${id}`
    return this.http
      .delete<void>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/deleteSkill API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getToolsWithPagination(
    limit: number = 50,
    nextToken?: string
  ): Promise<PaginatedResponse<LLMTool>> {
    return this.getPaginatedResults<LLMTool>('v2/tools', limit, nextToken)
  }

  getUserInstances(): Promise<UserInstance[]> {
    const url = `${DS_BASE_URL}/user-instances`
    return this.http
      .get<UserInstance[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getUserInstances API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getUserInstance(specialistUsername: string): Promise<UserInstance> {
    const url = `${DS_BASE_URL}/user-instances/${specialistUsername}`
    return this.http
      .get<UserInstance>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getUserInstance API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getAllSessions(
    specialistUsername: string,
    topN: number
  ): Promise<DigitalSpecialistSession[]> {
    const url = `${DS_BASE_URL}/chatSessions`
    return this.http
      .get<DigitalSpecialistSession[]>(url, {
        params: {
          specialistUsername,
          topN,
        },
      })
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getAllSessions API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  createSession(
    specialistUsername: string,
    message: string
  ): Promise<DigitalSpecialistSession> {
    // Trim length of message. If it's too long, request will be blocked
    message = message.substring(0, 20)
    const url = `${DS_BASE_URL}/chatSessions`
    return this.http
      .post<DigitalSpecialistSession>(url, {
        firstLineText: message,
        specialistUsername,
      })
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/createSession API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  deleteSession(sessionId: string): Promise<void> {
    const url = `${DS_BASE_URL}/chatSessions/${sessionId}`
    return this.http
      .delete<void>(`${DS_BASE_URL}/chatSessions/${sessionId}`)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/deleteSession API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getKnowledgeFiles(username: string): Promise<string[]> {
    const url = `${DS_BASE_URL}/knowledge/${username}`
    return this.http
      .get<string[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getKnowledgeFiles API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  uploadKnowledgeFile(username: string, file: File): Promise<void> {
    const form = new FormData()
    form.append('file', file)
    const url = `${DS_BASE_URL}/knowledge/${username}`
    return this.http
      .post<void>(url, form)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/uploadKnowledgeFile API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  deleteKnowledgeFiles(username: string, fileNames: string[]): Promise<void> {
    const url = `${DS_BASE_URL}/knowledge/${username}`
    return this.http
      .delete<void>(url, {
        body: fileNames,
      })
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/deleteKnowledgeFile API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  // ---- DS User permission specific methods ----
  getDsUsers(
    sharedResouceType: SharedResourceType,
    dsId: string
  ): Promise<DsUser[]> {
    const url = `${DS_BASE_URL}/permission/${sharedResouceType}/${dsId}`
    return this.http
      .get<DsUser[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/users API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  assignDsToUsers(
    sharedResouceType: SharedResourceType,
    resourceId: string,
    data: DsUser[]
  ) {
    const url = `${DS_BASE_URL}/permission/${sharedResouceType}/${resourceId}`
    return this.http
      .post(url, data)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/assignUsers API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  unassignDsFromUsers(
    sharedResouceType: SharedResourceType,
    resourceId: string,
    data: DsUser[]
  ) {
    const url = `${DS_BASE_URL}/permission/${sharedResouceType}/${resourceId}`
    return this.http
      .delete(url, { body: data })
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/unassignUsers API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  getParameterTypes() {
    const url = `${DS_BASE_URL}/parameters/types`
    return this.http
      .get<string[]>(url)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(`ds/getParameterTypes API failed, url: ${url}`)
          return Promise.reject(error)
        })
      )
      .toPromise()
  }

  private getPaginatedResults<T>(
    endpoint: string,
    limit: number = 50,
    nextToken?: string,
    additionalParams: Record<string, string> = {}
  ): Promise<PaginatedResponse<T>> {
    const validLimit = Math.max(1, Math.min(limit || 25, 100))

    const params = new URLSearchParams({
      limit: validLimit.toString(),
      ...additionalParams,
    })

    if (nextToken) {
      params.append('nextToken', nextToken)
    }

    const url = `${DS_BASE_URL}/${endpoint}?${params.toString()}`

    return this.http
      .get<PaginatedResponse<T>>(url)
      .pipe(
        take(1),
        catchError((error) => {
          if (error.status === 401 || error.status === 403) {
            console.error(
              `Authentication error in paginated request to ${endpoint}, token may have expired: ${error.message}`
            )
          } else {
            console.error(`Paginated API request failed, url: ${url}`, error)
          }
          return Promise.resolve({
            items: [],
            nextToken: undefined,
          } as PaginatedResponse<T>)
        })
      )
      .toPromise()
      .then((response) => {
        if (!response || !response.items) {
          console.warn('API response missing items array, creating empty array')
          return {
            items: [],
            nextToken: undefined,
          } as PaginatedResponse<T>
        }

        if (response.items.length > validLimit) {
          console.warn(
            `API returned more items (${response.items.length}) than requested (${validLimit}), truncating`
          )
          return {
            items: response.items.slice(0, validLimit),
            nextToken: response.nextToken,
          } as PaginatedResponse<T>
        }

        return response
      })
  }
}
